import { useEffect, useState } from 'react'
import { Select } from '@forwardflow/ui-v2'
import { FlowIcon } from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { Spinner } from '@forwardflow/features/campaign-flow/components/ui/primitives'
import {
   IContentOption,
   TContentSlot,
   TLeadType,
   TPlaybookContent,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { campaignFlowService } from '@forwardflow/features/campaign-flow/services'

const LABEL: Record<TContentSlot, string> = {
   script: 'Call script',
   messages: 'Texts and voicemails',
}

/**
 * Pick a call script or a outbound content group for one lead type: Forward Flow's
 * recommended ones or ones this organization made before. Picking one
 * replaces the words below; Undo changes brings the old ones back.
 */
export const ContentPicker = ({
   type,
   slot,
   currentId,
   currentName,
   edited,
   onPick,
}: {
   type: TLeadType
   slot: TContentSlot
   currentId?: string
   currentName: string
   /** The words were changed after the pick. */
   edited: boolean
   onPick: (content: Partial<TPlaybookContent>, option: IContentOption) => void
}) => {
   const [options, setOptions] = useState<IContentOption[] | null>(null)
   const [loading, setLoading] = useState(false)
   const [error, setError] = useState<string | null>(null)
   useEffect(() => {
      let live = true
      setOptions(null)
      void campaignFlowService
         .contentOptions(type, slot)
         .then((o) => live && setOptions(o))
         .catch(() => live && setOptions([]))
      return () => {
         live = false
      }
   }, [type, slot])

   const group = (owner: IContentOption['owner']) =>
      (options || [])
         .filter((o) => o.owner === owner)
         .map((o) => ({
            value: o.id,
            label: o.summary ? `${o.name} · ${o.summary}` : o.name,
         }))
   const data = [
      { group: 'Recommended', items: group('recommended') },
      { group: 'Made by you', items: group('yours') },
   ].filter((g) => g.items.length)
   const known = (options || []).some((o) => o.id === currentId)

   const pick = async (id: string | null) => {
      const option = options?.find((o) => o.id === id)
      if (!id || !option || id === currentId) return
      setLoading(true)
      setError(null)
      try {
         onPick(await campaignFlowService.loadContent(type, slot, id), option)
      } catch (e) {
         setError((e as Error).message || 'Could not open that one.')
      } finally {
         setLoading(false)
      }
   }

   return (
      <div
         className="flex flex-wrap items-center gap-x-3 gap-y-1.5"
         data-testid={`cf-pick-${slot}`}
      >
         <span className="text-[13px] text-[#9ca3af] flex items-center gap-1.5">
            <FlowIcon name={slot === 'script' ? 'phone' : 'chat'} size={13} />
            {LABEL[slot]}
         </span>
         <Select
            data={data}
            value={known ? currentId : null}
            placeholder={currentName || 'Pick one'}
            onChange={(v) => void pick(v)}
            searchable
            disabled={!options || loading}
            rightSection={
               !options || loading ? <Spinner size={12} /> : undefined
            }
            className="w-full sm:w-[340px]"
            comboboxProps={{ withinPortal: true }}
            aria-label={LABEL[slot]}
            data-testid={`cf-pick-${slot}-select`}
         />
         {edited && (
            <span
               className="text-xs text-[#F5C97A]"
               data-testid={`cf-pick-${slot}-edited`}
            >
               Edited for this campaign
            </span>
         )}
         {error && <span className="text-xs text-amber-300">{error}</span>}
      </div>
   )
}
